/**
 * React hook to Determine how a layerhost should invoke a dxl Query
 *
 * @module utils/useLayerHost
 */

import { useEffect, useState } from 'react';

/**
 * Method to get required client params, which includes moduleParams & set skip
 * @param  {object} data
 * @param  {func} methods
 */
export const getRequiredClientParams = ( data, methods ) => {
  const { user = {}, retailerVisitorId = '', environment = 'd', action = {} } = data || {};
  const { setSkip } = methods || {};

  let skip = false;
  const moduleParams = {};
  const { params } = action;

  if( typeof setSkip !== 'function' ){
    return action;
  }

  if( !Array.isArray( params ) ){
    setSkip( false );
    return action;
  }

  for ( const param of params ){
    switch ( param ){
      case 'gti':
        skip = !user.gti;
        moduleParams[param] = user[param];
        break;
      case 'loginStatus':
        moduleParams[param] = user.loginType;
        break;
      case 'environment':
        moduleParams[param] = environment;
        break;
      case 'retailerVisitorId':
        moduleParams[param] = retailerVisitorId;
        break;
    }
  }

  if( !skip ){
    setSkip( false );
  }

  return {
    ...action,
    variables: {
      moduleParams
    },
    skip
  };
};

/**
 * Method to create and dispatch custom event 'openLoginModal' custom event also
 * @param  {string} successPath
 */
export const useLayerHostSkip = function( methods, data ){
  const [skip, setSkip] = useState( true );
  const [mutation, setMutation] = useState( {
    graphql: `mutation{
      NotifyMe(url:"/bag/poc", moduleParams: {loginStatus:"anonymous"}) {
      content
      meta
      }
      }`
  } );

  const [query, setQuery] = useState( {
    graphql: `query Page($moduleParams: JSON) {
      Page(moduleParams:$moduleParams) {
        content
      }
      }`,
    variables: {}
  } );

  useEffect( () => {
    // check if there's an initAction that existed
    // the component will invoke it immediately to refresh
    // the view
    if( !data.meta?.initAction || !skip ){
      return;
    }

    const paramData = {
      user: data.user,
      action: data.meta.initAction,
      retailerVisitorId: data.retailerVisitorId,
      environment: data.environment
    };

    const action = getRequiredClientParams( paramData, { setSkip } );
    setQuery( action );
  }, [data.meta, data.user] );

  // listens for when a broadcat happens and compare if it matches,
  // if so we should refresh the content and in turn update the view
  useEffect( () => {
    if( !data.refreshContentId || data.refreshContentId !== data.id ){
      return;
    }

    setSkip( false );

    if( data.refreshAction ){
      setQuery( data.refreshAction );
    }
    else if( data.data ){
      // set the data that has been alreayd retried and push it into the layerhost for display update
      methods.setData( data.data );
    }
    methods.clearBroadcastModuleUpdate();
  }, [data.refreshContentId] );

  useEffect( () => {
    if( !data.invokeQuery ){
      return;
    }

    // TODO if the query doesn't have a contentId we should make a query with the provided contentID
    // TODO the contentID in dxl right now is not a param, but in the query, os we need to address that to easily get access to it
    if( !data.invokeQuery.graphql?.match( /contentId:\s?"/ ) && !data.invokeQuery.url ){
      setSkip( true );
      methods.broadcastModuleUpdate( undefined, {
        contentId: data.containerLayerHostContentId,
        action: data.invokeQuery
      } );
    }
    else if( skip ){
      setSkip( false );
      setQuery( data.invokeQuery );
    }

    if( !skip && !data.invokeQuery.url ){
      setQuery( data.invokeQuery );
    }
  }, [data.invokeQuery] );

  useEffect( () => {
    if( !data.invokeMutation ){
      return;
    }

    setMutation( {
      graphql: data.invokeMutation.graphql,
      variables: data.invokeMutation.variables
    } );
  }, [data.invokeMutation] );

  return [skip, mutation, query];
};
